/**
 * components/ProcedimientoVisor.js
 * Modal con visor de PDF/imagen y tracking de scroll
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { FaTimes, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';

const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4004' 
  : '';

const ProcedimientoVisor = ({ show, onHide, procedimiento }) => {
  const [loading, setLoading] = useState(true);
  const [scrollCompleto, setScrollCompleto] = useState(false);
  const [openRegistrado, setOpenRegistrado] = useState(false);
  const containerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const registrarApertura = useCallback(async () => {
    if (!procedimiento) return;
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/procedimientos/${procedimiento.id}/open`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setOpenRegistrado(true);
        console.log('✅ Apertura registrada');
      }
    } catch (err) {
      console.error('Error registrando apertura:', err);
    }
  }, [procedimiento]);

  useEffect(() => {
    if (show && procedimiento) {
      setLoading(true); // Reset loading cuando se abre
      registrarApertura();
      setScrollCompleto(!!procedimiento.scroll_completed_at);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [show, procedimiento, registrarApertura]);

  const handleScroll = () => {
    if (!containerRef.current || scrollCompleto || procedimiento.requiere_scroll_fin === 0) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const scrollPercentage = ((scrollTop + clientHeight) / scrollHeight) * 100;

    // Si llega al 95%, registrar scroll completado
    if (scrollPercentage >= 95) {
      // Debounce para evitar múltiples llamadas
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        registrarScrollCompleto();
      }, 500);
    }
  };

  const registrarScrollCompleto = async () => {
    if (scrollCompleto) return;

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/procedimientos/${procedimiento.id}/scroll-end`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setScrollCompleto(true);
        toast.success('✅ Lectura completa registrada');
        console.log('✅ Scroll completo registrado');
      }
    } catch (err) {
      console.error('Error registrando scroll:', err);
    }
  };

  const getFileUrl = () => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/procedimientos/${procedimiento.id}/file?token=${token}`;
  };

  const isPDF = procedimiento.archivo_path?.toLowerCase().endsWith('.pdf');
  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(procedimiento.archivo_path || '');

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>
          {procedimiento.titulo} <small className="text-muted">v{procedimiento.version}</small>
        </Modal.Title>
        <Button variant="link" onClick={onHide}>
          <FaTimes />
        </Button>
      </Modal.Header>

      <Modal.Body style={{ height: '70vh', overflowY: 'auto' }} ref={containerRef} onScroll={handleScroll}>
        {procedimiento.requiere_scroll_fin === 1 && (
          <Alert variant={scrollCompleto ? 'success' : 'warning'} className="mb-3">
            {scrollCompleto ? (
              <>
                <FaCheckCircle /> Lectura completa registrada
              </>
            ) : (
              <>
                ⚠️ Debés leer todo el documento hasta el final para poder confirmar
              </>
            )}
          </Alert>
        )}

        {isPDF && (
          <iframe
            src={getFileUrl()}
            style={{ width: '100%', height: '100%', minHeight: '600px', border: 'none' }}
            title={procedimiento.titulo}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              toast.error('Error al cargar el PDF');
            }}
          />
        )}

        {isImage && (
          <div className="text-center">
            <img
              src={getFileUrl()}
              alt={procedimiento.titulo}
              style={{ maxWidth: '100%', height: 'auto' }}
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                toast.error('Error al cargar la imagen');
              }}
            />
          </div>
        )}

        {!isPDF && !isImage && (
          <Alert variant="warning">
            No se puede visualizar este tipo de archivo. Usá el botón de descarga.
          </Alert>
        )}

        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">Cargando documento...</p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="w-100 d-flex justify-content-between align-items-center">
          <div>
            {openRegistrado && (
              <small className="text-success">
                <FaCheckCircle /> Apertura registrada
              </small>
            )}
          </div>
          <Button variant="secondary" onClick={onHide}>
            Cerrar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ProcedimientoVisor;
